import { useState, useCallback, useRef, type ReactNode, type DragEvent, type ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface LogoUploadStepProps {
  readonly currentLogoUrl: string | null;
  readonly onComplete: (file: File | null) => void;
  readonly onSkip: () => void;
}

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];
const MAX_SIZE = 2 * 1024 * 1024; // 2MB

export function LogoUploadStep({ currentLogoUrl, onComplete, onSkip }: LogoUploadStepProps): ReactNode {
  const [preview, setPreview] = useState<string | null>(currentLogoUrl);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("\u05E4\u05D5\u05E8\u05DE\u05D8 \u05DC\u05D0 \u05E0\u05EA\u05DE\u05DA. \u05D4\u05E9\u05EA\u05DE\u05E9\u05D5 \u05D1-PNG, JPG, SVG \u05D0\u05D5 WebP");
      return;
    }
    if (file.size > MAX_SIZE) {
      setError("\u05D4\u05E7\u05D5\u05D1\u05E5 \u05D2\u05D3\u05D5\u05DC \u05DE\u05D3\u05D9. \u05DE\u05E7\u05E1\u05D9\u05DE\u05D5\u05DD 2MB");
      return;
    }
    setError(null);
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreview(url);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  return (
    <div className="flex flex-col gap-6 px-4 py-6">
      <h2 className="text-xl font-bold">{"\u05DC\u05D5\u05D2\u05D5"}</h2>

      <Card>
        <CardContent className="flex flex-col items-center gap-4 p-4">
          {preview !== null ? (
            <div className="flex flex-col items-center gap-3">
              <img
                src={preview}
                alt="Logo preview"
                className="h-32 w-32 rounded-lg border object-contain"
              />
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px]"
                onClick={() => inputRef.current?.click()}
              >
                {"\u05D4\u05D7\u05DC\u05E3 \u05DC\u05D5\u05D2\u05D5"}
              </Button>
            </div>
          ) : (
            <div
              className={`flex w-full cursor-pointer flex-col items-center gap-3 rounded-lg border-2 border-dashed p-8 transition-colors ${
                dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25"
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
              </svg>
              <p className="text-sm text-muted-foreground">
                {"\u05D2\u05E8\u05E8\u05D5 \u05E7\u05D5\u05D1\u05E5 \u05DC\u05DB\u05D0\u05DF \u05D0\u05D5 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05D1\u05D7\u05D9\u05E8\u05D4"}
              </p>
              <p className="text-xs text-muted-foreground/60">PNG, JPG, SVG, WebP | 2MB max</p>
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            accept=".png,.jpg,.jpeg,.svg,.webp"
            onChange={handleChange}
            className="hidden"
          />

          {error !== null && <p className="text-sm text-destructive">{error}</p>}

          {currentLogoUrl !== null && selectedFile === null && (
            <Button
              variant="secondary"
              className="min-h-[44px] w-full"
              onClick={() => onComplete(null)}
            >
              {"\u05D4\u05E9\u05EA\u05DE\u05E9 \u05D1\u05DC\u05D5\u05D2\u05D5 \u05D4\u05E0\u05D5\u05DB\u05D7\u05D9"}
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button variant="outline" className="min-h-[44px] flex-1" onClick={onSkip}>
          {"\u05D3\u05DC\u05D2"}
        </Button>
        {selectedFile !== null && (
          <Button className="min-h-[44px] flex-1" onClick={() => onComplete(selectedFile)}>
            {"\u05D4\u05DE\u05E9\u05DA"}
          </Button>
        )}
      </div>
    </div>
  );
}
